import { useSyncExternalStore } from "react";
import { Alert, AlertTitle, Box, Button, Stack } from "@mui/material";
import { useTranslation } from "i18n-fe";
import {
  dismissQuarantineNotice,
  getQuarantineNotices,
  subscribeQuarantineNotices,
} from "../store/quarantineNotices";

// #3155 -- the scribe-visible half of a quarantined sheet write (#2812). When
// score-api refuses a write with a 400 `problems` list (an otherDeduction that
// is notEnriched / over its cap / unknown / mismatched) sync.js quarantines the
// op and removes it from the outbox; this banner is the only trace the scribe
// gets, so it names the refused deduction in scribe language. Kiosk rules:
// non-blocking (fixed to the bottom, never a modal that steals focus mid-mark),
// always dismissible with a plain OK, never a dead end, and every i18n key is a
// static literal.
export default function QuarantineNotices() {
  const { t } = useTranslation("score");
  const notices = useSyncExternalStore(
    subscribeQuarantineNotices,
    getQuarantineNotices,
    getQuarantineNotices,
  );
  if (notices.length === 0) return null;
  return (
    <Box
      data-testid="quarantine-notices"
      sx={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1200,
        p: 1,
        pointerEvents: "none",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Stack spacing={1} sx={{ width: "100%", maxWidth: 640, pointerEvents: "auto" }}>
        {notices.map((n) => (
          <Alert
            key={n.noticeId}
            severity="warning"
            variant="outlined"
            sx={{ bgcolor: "background.paper" }}
            data-testid="quarantine-notice"
            action={
              <Button
                color="inherit"
                size="small"
                data-testid="quarantine-notice-dismiss"
                onClick={() => dismissQuarantineNotice(n.noticeId)}
              >
                {t("quarantineNotice.ok")}
              </Button>
            }
          >
            <AlertTitle>
              {n.removed
                ? n.rideLabel
                  ? t("quarantineNotice.titleRideRemoved", { ride: n.rideLabel })
                  : t("quarantineNotice.titleRemoved")
                : n.rideLabel
                  ? t("quarantineNotice.titleRide", { ride: n.rideLabel })
                  : t("quarantineNotice.title")}
            </AlertTitle>
            {n.problems.map((p, i) => (
              <Box key={`${p.id}:${i}`} data-testid="quarantine-notice-problem" sx={{ mb: 0.5 }}>
                {!n.removed &&
                  p.reason === "notEnriched" &&
                  t("quarantineNotice.notEnriched", { label: p.label })}
                {!n.removed && p.reason === "cap" && t("quarantineNotice.cap", { label: p.label })}
                {!n.removed &&
                  p.reason !== "notEnriched" &&
                  p.reason !== "cap" &&
                  t("quarantineNotice.other", { label: p.label })}
                {n.removed &&
                  p.reason === "notEnriched" &&
                  t("quarantineNotice.removedNotEnriched", { label: p.label })}
                {n.removed && p.reason === "cap" && t("quarantineNotice.removedCap", { label: p.label })}
                {n.removed &&
                  p.reason !== "notEnriched" &&
                  p.reason !== "cap" &&
                  t("quarantineNotice.removedOther", { label: p.label })}
              </Box>
            ))}
            <Box>{n.removed ? t("quarantineNotice.removedFooter") : t("quarantineNotice.footer")}</Box>
          </Alert>
        ))}
      </Stack>
    </Box>
  );
}
